/**
 * FBA economics — pure functions, no I/O.
 *
 * Fee tables are DATA and change yearly. Current tables: US 2026 rate card
 * (non-peak, non-apparel, non-dangerous goods), effective 2026-01-15, plus the
 * 3.5% fuel & logistics surcharge effective 2026-04-17.
 * Source: Amazon Seller Central 2026 US FBA fee changes (GABBX6GZPA8MSZGW).
 * Update FEES when Amazon publishes a new card.
 */

export const FEE_SCHEDULE_VERSION = '2026-US-standard';

export const FUEL_SURCHARGE_RATE = 0.035; // since 2026-04-17, applied on fulfillment fee

// Price bands used by the 2026 fulfillment fee card.
function priceBand(price) {
  if (price < 10) return 0;
  if (price <= 50) return 1;
  return 2;
}

// [maxWeightLb, [<$10, $10–50, >$50]] — non-peak, non-apparel.
const SMALL_STANDARD = [
  [0.125, [2.43, 3.32, 3.58]], // ≤2 oz
  [0.25,  [2.49, 3.42, 3.68]],
  [0.375, [2.56, 3.45, 3.71]],
  [0.5,   [2.66, 3.54, 3.80]],
  [0.625, [2.77, 3.68, 3.94]],
  [0.75,  [2.82, 3.78, 4.04]],
  [0.875, [2.92, 3.91, 4.17]],
  [1.0,   [2.95, 3.96, 4.22]],
];

const LARGE_STANDARD = [
  [0.25,  [2.91, 3.73, 3.99]], // ≤4 oz
  [0.5,   [3.13, 3.95, 4.21]],
  [0.75,  [3.38, 4.20, 4.46]],
  [1.0,   [3.78, 4.60, 4.86]],
  [1.25,  [4.22, 5.04, 5.30]],
  [1.5,   [4.60, 5.42, 5.68]],
  [1.75,  [4.75, 5.57, 5.83]],
  [2.0,   [5.00, 5.82, 6.08]],
  [2.25,  [5.10, 5.92, 6.18]],
  [2.5,   [5.28, 6.10, 6.36]],
  [2.75,  [5.44, 6.26, 6.52]],
  [3.0,   [5.85, 6.67, 6.93]],
];
// 3–20 lb: base + $0.08 per 4 oz (0.25 lb) above 3 lb.
const LARGE_STANDARD_OVER_3LB_BASE = [6.15, 6.97, 7.23];
const OVER_3LB_INCREMENT = 0.08;

// Referral fees: rate by category (top categories; default 15%). Min fee $0.30.
export const REFERRAL_MIN_FEE = 0.30;
export const REFERRAL_RATES = {
  default: () => 0.15,
  electronics: () => 0.08,
  camera: () => 0.08,
  computers: () => 0.08,
  video_games: () => 0.15,
  home: () => 0.15,
  kitchen: () => 0.15,
  toys: () => 0.15,
  grocery: (price) => (price <= 15 ? 0.08 : 0.15),
  health: (price) => (price <= 10 ? 0.08 : 0.15),
  beauty: (price) => (price <= 10 ? 0.08 : 0.15),
  clothing: (price) => (price <= 15 ? 0.05 : price <= 20 ? 0.10 : 0.17),
  watches: (price) => (price > 1500 ? 0.03 : 0.16),
  jewelry: (price) => (price > 250 ? 0.05 : 0.20),
};

// Monthly storage $/cu ft (standard size, base utilization band).
export const STORAGE_RATES = { offPeak: 0.78, peak: 2.40 }; // peak = Oct–Dec

export function referralFee(price, category = 'default') {
  if (!(price > 0)) return 0;
  const rateFn = REFERRAL_RATES[category] || REFERRAL_RATES.default;
  return Math.max(round2(price * rateFn(price)), REFERRAL_MIN_FEE);
}

/**
 * Size tier from dimensions (inches, any order) and unit weight (lb).
 * Small standard: ≤15×12×0.75 in and ≤1 lb. Large standard: ≤18×14×8 in and ≤20 lb.
 * Anything bigger → 'oversize' (fees not modeled here).
 */
export function getSizeTier({ lengthIn, widthIn, heightIn, weightLb }) {
  const dims = [lengthIn, widthIn, heightIn].map(Number).sort((a, b) => b - a);
  if (dims.some((d) => !(d > 0)) || !(weightLb > 0)) return null;
  const [l, w, h] = dims;
  if (l <= 15 && w <= 12 && h <= 0.75 && weightLb <= 1) return 'small_standard';
  if (l <= 18 && w <= 14 && h <= 8 && weightLb <= 20) return 'large_standard';
  return 'oversize';
}

/**
 * Shipping weight: small standard uses unit weight; large standard uses the
 * greater of unit weight and dimensional weight (l×w×h / 139).
 */
export function shippingWeight(tier, { lengthIn, widthIn, heightIn, weightLb }) {
  if (tier === 'large_standard') {
    const dimWeight = (lengthIn * widthIn * heightIn) / 139;
    return Math.max(weightLb, dimWeight);
  }
  return weightLb;
}

export function fulfillmentFee({ tier, shippingWeightLb, price, fuelSurcharge = true }) {
  const band = priceBand(price);
  let fee = null;

  const lookup = (table) => {
    for (const [maxW, fees] of table) {
      if (shippingWeightLb <= maxW) return fees[band];
    }
    return null;
  };

  if (tier === 'small_standard') {
    fee = lookup(SMALL_STANDARD);
  } else if (tier === 'large_standard') {
    fee = lookup(LARGE_STANDARD);
    if (fee == null && shippingWeightLb <= 20) {
      const intervals = Math.ceil((shippingWeightLb - 3) / 0.25);
      fee = LARGE_STANDARD_OVER_3LB_BASE[band] + intervals * OVER_3LB_INCREMENT;
    }
  }
  if (fee == null) return null; // oversize / out of modeled range

  if (fuelSurcharge) fee *= 1 + FUEL_SURCHARGE_RATE;
  return round2(fee);
}

/** Monthly storage fee for one unit. monthOfSale: 1–12. */
export function storageFee({ lengthIn, widthIn, heightIn, months = 1, monthOfSale = new Date().getMonth() + 1 }) {
  const cubicFt = (lengthIn * widthIn * heightIn) / 1728;
  if (!(cubicFt > 0) || !(months > 0)) return 0;
  const rate = monthOfSale >= 10 ? STORAGE_RATES.peak : STORAGE_RATES.offPeak;
  return round2(cubicFt * rate * months);
}

/**
 * Full FBA profitability analysis for one unit.
 * Returns null fields + supported:false when the item is oversize.
 */
export function analyzeFBA({
  salePrice,
  cost,
  inboundShipping = 0,
  category = 'default',
  weightLb,
  lengthIn,
  widthIn,
  heightIn,
  storageMonths = 1,
  monthOfSale,
  fuelSurcharge = true,
}) {
  const dims = { lengthIn, widthIn, heightIn, weightLb };
  const tier = getSizeTier(dims);
  if (!tier || tier === 'oversize') {
    return { supported: false, sizeTier: tier, feeScheduleVersion: FEE_SCHEDULE_VERSION };
  }

  const shipWeight = shippingWeight(tier, dims);
  const fulfillment = fulfillmentFee({ tier, shippingWeightLb: shipWeight, price: salePrice, fuelSurcharge });
  const referral = referralFee(salePrice, category);
  const storage = storageFee({ lengthIn, widthIn, heightIn, months: storageMonths, monthOfSale });

  const totalFees = round2(referral + fulfillment + storage);
  const netProfit = round2(salePrice - totalFees - cost - inboundShipping);
  const marginPct = salePrice > 0 ? round2((netProfit / salePrice) * 100) : 0;
  const denom = cost + inboundShipping;
  const roiPct = denom > 0 ? round2((netProfit / denom) * 100) : null;

  return {
    supported: true,
    sizeTier: tier,
    shippingWeightLb: round2(shipWeight),
    referralFee: referral,
    fulfillmentFee: fulfillment,
    storageFee: storage,
    totalFees,
    netProfit,
    marginPct,
    roiPct,
    breakevenPrice: breakevenPrice({ cost, inboundShipping, category, tier, shippingWeightLb: shipWeight, dims, storageMonths, monthOfSale, fuelSurcharge }),
    feeScheduleVersion: FEE_SCHEDULE_VERSION,
  };
}

/**
 * Sale price where net profit = 0. Referral is proportional and the
 * fulfillment fee is a step function of the price band, so solve per band and
 * pick the consistent fixed point (two passes suffice).
 */
function breakevenPrice({ cost, inboundShipping, category, tier, shippingWeightLb, dims, storageMonths, monthOfSale, fuelSurcharge }) {
  const storage = storageFee({ ...dims, months: storageMonths, monthOfSale });
  let price = Math.max(cost + inboundShipping, 1); // seed guess

  for (let i = 0; i < 3; i++) {
    const fulfillment = fulfillmentFee({ tier, shippingWeightLb, price, fuelSurcharge });
    if (fulfillment == null) return null;
    const rateFn = REFERRAL_RATES[category] || REFERRAL_RATES.default;
    const r = rateFn(price);
    const fixed = cost + inboundShipping + fulfillment + storage;
    let candidate = fixed / (1 - r);
    // Respect the $0.30 referral minimum for very cheap items.
    if (candidate * r < REFERRAL_MIN_FEE) candidate = fixed + REFERRAL_MIN_FEE;
    if (Math.abs(candidate - price) < 0.01) return round2(candidate);
    price = candidate;
  }
  return round2(price);
}

function round2(n) {
  return Math.round(n * 100) / 100;
}
